import { create } from 'jss';
import jssPreset from '@mui/styles/jssPreset';

import type { ClientApi } from '@openclassrooms/sdk-web';
import browser from '@openclassrooms/utils/browser';

import baseRichContentStyles from '../styles/base';

import codeHighlight from '../code-highlight';
import maths from '../math';
import handleFormAssemblyLinks from '../form-assembly/services/handleFormAssemblyLinks';
import handleRootmeLinks from '../root-me/services/handleRootmeLinks';
import handleCompanionLinks from '../companion/services/handleCompanionLinks';
import handleCourseLinks from '../course-link/services/handleCourseLinks';
import handleChapterEmbedding from '../embedded-chapter/services/handleChapterEmbedding';
import claireSemantic from '../claire/services/semantic';
import smileys from '../smileys';
import images from '../images';
import videos from '../videos';
import codeSandbox from '../code-sandbox';
import handleRootmeV2Links from '../root-me-v2/services/handleRootmeV2Links';
import { anchors } from '../anchors';
import table from '../table';

export type ParsersOptions = {
  shouldLockMediaContent?: boolean;
};

export type ParsersApi = {
  offlineOptions?: Record<string, unknown>;
  parse: (
    domContainer: HTMLElement,
    client?: ClientApi,
    projectId?: number,
    styles?: object | null,
    options?: ParsersOptions
  ) => void;
  parseVideos: (domContainer: HTMLElement, shouldLockMediaContent?: boolean) => void;
  parseVideosOffline: (domContainer: HTMLElement) => void;
  parseCodes: (domContainer: HTMLElement) => void;
  parseCodeSandbox: (domContainer: HTMLElement, shouldLockMediaContent?: boolean) => void;
  parseMaths: (domContainer: HTMLElement) => void;
  parseEmojis: (domContainer: HTMLElement) => void;
  parseTables: (domContainer: HTMLElement) => void;
  parseImages: (domContainer: HTMLElement) => void;
  parseImagesOffline: (domContainer: HTMLElement) => void;
  parseTitlesWithAnchors: (domContainer: HTMLElement) => void;
  parseCompanionLinks: (domContainer: HTMLElement, projectId?: number) => void;
  parseCourseLinks: (domContainer: HTMLElement, client?: ClientApi) => void;
  parseEmbeddedChapters: (domContainer: HTMLElement, client?: ClientApi) => void;
  parseRootMeLinks: (domContainer: HTMLElement, client?: ClientApi) => void;
  parseRootMeV2Links: (domContainer: HTMLElement) => void;
  parseOffline: (
    domContainer: HTMLElement,
    offlineOptions?: Record<string, unknown>,
    styles?: object
  ) => void;
  parseFormAssemblyLinks: (domContainer: HTMLElement) => void;
  clearImagesZoom: () => void;
  useStyles: (domContainer: HTMLElement, styles?: object) => void;
};

const attachStyles = (domContainer: HTMLElement, styles: any) => {
  if (domContainer.classList.contains('oc-richContent')) return;
  const jss = create(jssPreset());
  const sheet = jss.createStyleSheet(styles as any);
  sheet.attach();
  domContainer.classList.add('oc-richContent');
  if ((sheet as any).classes?.root) {
    domContainer.classList.add((sheet as any).classes.root);
  }
};

const CLAIRE_TO_ACE_LANGUAGES: Record<string, string> = {
  c: 'c_cpp',
  'html+php': 'php',
  cpp: 'c_cpp',
  nasm: 'assembly_x86',
  'objective-c': 'objectivec',
};

const getAceLanguageFromClaireLanguage = (claireLanguage: string) =>
  CLAIRE_TO_ACE_LANGUAGES[claireLanguage] || claireLanguage;

export const parsers: ParsersApi = {
  offlineOptions: {},

  parseVideos(domContainer, shouldLockMediaContent) {
    videos.init(domContainer, Boolean(shouldLockMediaContent));
  },

  parseVideosOffline(domContainer) {
    const videosToParse = Array.from(domContainer.getElementsByTagName('video'));

    const videoOptions = (this.offlineOptions?.videos as Record<string, string>) || {};
    const useOnlineFallback = Boolean(this.offlineOptions?.useOnlineFallback);

    videosToParse.forEach((video) => {
      const fallbackSrc = videoOptions[(video as any).id];
      if (fallbackSrc) {
        if (!browser.document) {
          return;
        }

        (video as any).setAttribute('src', fallbackSrc);
        (video as any).setAttribute('controls', '1');
        (video as any).setAttribute('class', 'oc-video__iframe');

        const videoWrapper = browser.document.createElement('div');
        videoWrapper.className = 'oc-video';
        (video.parentNode as any).insertBefore(videoWrapper, video);
        videoWrapper.appendChild(video);
      } else if (useOnlineFallback) {
        videos.parseVideo(video);
      }
    });
  },

  parseCodes(domContainer) {
    const codes = (Array.from(domContainer.querySelectorAll('pre code')).filter((code: Element) =>
      Boolean(claireSemantic.getClaireSemantic(code))
    ) || []) as HTMLElement[];

    codes.forEach((code) => {
      if (code.dataset.highlighted) return;
      // eslint-disable-next-line no-param-reassign
      code.dataset.highlighted = 'true';

      const claireLanguage = claireSemantic.getClaireSemantic(code);
      const language = getAceLanguageFromClaireLanguage(claireLanguage);
      codeHighlight.syntaxHighlight(code, language, true);
    });
  },

  parseCodeSandbox(domContainer, shouldLockMediaContent) {
    codeSandbox.init(domContainer, Boolean(shouldLockMediaContent));
  },

  parseCompanionLinks(domContainer, projectId) {
    handleCompanionLinks(domContainer, projectId);
  },

  parseCourseLinks(domContainer, client) {
    handleCourseLinks(domContainer, client);
  },

  parseEmbeddedChapters(domContainer, client) {
    // Re-parse embedded chapter HTML so rich-content features (videos, links, maths, etc.)
    // are applied after mount, while avoiding a direct component -> parsers import cycle.
    const parseRichEmbeddedContent = (
      embeddedDomContainer: HTMLElement,
      embeddedClient?: ClientApi,
      embeddedProjectId?: number,
      embeddedStyles?: object | null,
      embeddedOptions: ParsersOptions = {}
    ) =>
      this.parse(
        embeddedDomContainer,
        embeddedClient,
        embeddedProjectId,
        embeddedStyles,
        embeddedOptions
      );

    handleChapterEmbedding(domContainer, client, parseRichEmbeddedContent);
  },

  parseRootMeLinks(domContainer, client) {
    handleRootmeLinks(domContainer, client!);
  },

  parseRootMeV2Links(domContainer) {
    handleRootmeV2Links(domContainer);
  },

  parseMaths(domContainer) {
    const mathsNodes = Array.from(domContainer.getElementsByTagName('math'));
    if (!mathsNodes.length) return;

    const mathsElements = mathsNodes.flatMap((math) => {
      if (!browser.document || !math.parentNode) {
        return [];
      }

      const span = browser.document.createElement('span');
      span.innerHTML = math.innerHTML;
      math.parentNode.insertBefore(span, math);
      math.parentNode.removeChild(math);

      return [span];
    });

    maths.mathjax.highlight(mathsElements);
  },

  parseEmojis(domContainer) {
    smileys.init(domContainer);
  },

  parseTables(domContainer) {
    table.wrapTables(domContainer);
  },

  parseImages(domContainer) {
    images.init(domContainer);
  },

  parseImagesOffline(domContainer) {
    const pictures = Array.from(domContainer.querySelectorAll('img'));
    const imageOptions = (this.offlineOptions?.images as Record<string, string>) || {};
    pictures.forEach((picture) => {
      const fallbackSrc = imageOptions[(picture as any).id];
      if (fallbackSrc) picture.setAttribute('src', fallbackSrc);
    });
    this.parseImages(domContainer);
  },

  parseTitlesWithAnchors(domContainer) {
    anchors.createAnchors(domContainer);
  },

  parseFormAssemblyLinks(domContainer) {
    handleFormAssemblyLinks(domContainer);
  },

  useStyles(domContainer, styles = baseRichContentStyles) {
    if (!domContainer) return;
    attachStyles(domContainer, styles);
  },

  parse(
    domContainer,
    client,
    projectId,
    styles = baseRichContentStyles,
    options: ParsersOptions = {}
  ) {
    const { shouldLockMediaContent } = options;
    this.offlineOptions = this.offlineOptions || {};
    const AVAILABLE_PARSERS = {
      VIDEOS: 'parseVideos',
      CODE: 'parseCodes',
      MATHS: 'parseMaths',
      IMAGES: 'parseImages',
      EMOJIS: 'parseEmojis',
      TABLES: 'parseTables',
      SANDBOX: 'parseCodeSandbox',
      COMPANION: 'parseCompanionLinks',
      COURSE_LINKS: 'parseCourseLinks',
      EMBEDDED_CHAPTERS: 'parseEmbeddedChapters',
      ROOTME: 'parseRootMeLinks',
      ROOTME_V2: 'parseRootMeV2Links',
      TITLES_ANCHORS: 'parseTitlesWithAnchors',
      FORM_ASSEMBLY: 'parseFormAssemblyLinks',
    } as const;

    if (styles) {
      this.useStyles(domContainer, styles);
    }

    Object.values(AVAILABLE_PARSERS).forEach((method) => {
      try {
        if (method === AVAILABLE_PARSERS.VIDEOS || method === AVAILABLE_PARSERS.SANDBOX) {
          (this as any)[method].call(this, domContainer, shouldLockMediaContent);
        } else if (method === AVAILABLE_PARSERS.COMPANION) {
          (this as any)[method].call(this, domContainer, projectId);
        } else {
          (this as any)[method].call(this, domContainer, client);
        }
      } catch {
        // eslint-disable-next-line no-empty
      }
    });

    anchors.scrollToAnchor(domContainer);
  },

  parseOffline(domContainer, offlineOptions = {}, styles = baseRichContentStyles) {
    this.offlineOptions = offlineOptions;

    if (styles) {
      this.useStyles(domContainer, styles);
    }

    [
      'parseVideosOffline',
      'parseCodes',
      'parseMaths',
      'parseImagesOffline',
      'parseEmojis',
      'parseTables',
    ].forEach((method) => (this as any)[method].call(this, domContainer));
  },

  clearImagesZoom: images.zoom.clear,
};
